<script setup lang="ts">
import underReview from '@/assets/icons/under-review.svg';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useKycSessionStore } from '@/stores/kycSession.store';
import { KycCheckStatus, KycSessionStatus } from '@/common/types';
import { AmethystClient } from '@/integrations/Immersve/AmethystClient';
import Loading from '@/components/ui/Loading.vue';
import { wellKnownKycSessionRoutes } from '@/routes/wellKnownKycSessionRoutes';
import KycSessionLayout from '@/layout/KycSessionLayout.vue';
import { config } from '@/config';
import { Environment } from '@/util/Environment';

const isLoading = ref(true);

const kycSessionStore = useKycSessionStore();
const { kycSession } = storeToRefs(kycSessionStore);

const router = useRouter();
let polling = false;
let timerId;

function calculateTimeoutInterval({
  flatnessFactor = 0.95,
  baseDelayInMillis,
  maxDelayInMillis,
  callNumber,
}: {
  flatnessFactor?: number;
  baseDelayInMillis: number;
  callNumber: number;
  maxDelayInMillis: number;
}) {
  const flatness = callNumber < 5 ? flatnessFactor : flatnessFactor * 0.3;

  return Math.min(
    flatness * Math.pow(baseDelayInMillis / 1000, callNumber) * 1000,
    maxDelayInMillis
  );
}

function visibilityChangeHandler() {
  //the window is visible i.e. the user is using it
  if (document.visibilityState === 'visible') {
    // a timer is alive and we want to be polling. nothing to do
    if (timerId && polling) {
      return;
    }
    startPolling();
    return;
  }
  //the window is not visible i.e. the user is doing something else
  // a timer doesn't exist and we do not want to be polling. nothing to do
  if (!timerId && !polling) {
    return;
  }

  stopPolling();
}

async function startPolling() {
  polling = true;
  for (let i = 0; ; i++) {
    const delay = calculateTimeoutInterval({
      baseDelayInMillis: 2000,
      maxDelayInMillis: 30000,
      callNumber: i,
    });
    try {
      await new Promise((inner) => (timerId = setTimeout(inner, delay)));
      await kycSessionStore.getSessionData();
    } catch {
      break;
    }
  }
}

function stopPolling() {
  console.log('stopping polling'); //eslint-disable-line no-console
  polling = false;
  clearInterval(timerId);
}

async function initiateCheck() {
  if (!kycSessionStore.kycSession?.id) {
    throw new Error('Could not find session id');
  }

  //complete
  if (
    Environment.useInTestEnvironmentOtherwiseTrue(config.completeKycSessionEnabled) &&
    kycSessionStore.kycSession?.status !== KycSessionStatus.COMPLETED
  ) {
    await new AmethystClient().completeKycSession(kycSessionStore.kycSession.id);
  }

  isLoading.value = false;

  if (
    kycSessionStore.kycSession.status === KycSessionStatus.COMPLETED &&
    kycSessionStore.kycSession.kycCheck?.status &&
    isKycCheckComplete(kycSessionStore.kycSession.kycCheck.status)
  ) {
    await redirectToFinalScreen(kycSessionStore.kycSession.kycCheck.status);
  } else {
    startPolling();
    watch(
      [
        () => kycSessionStore.kycSession?.kycCheck?.status,
        () => kycSessionStore.kycSession?.status,
      ],
      async () =>
        kycSessionStore.kycSession?.kycCheck?.status &&
        (await handleChangedKycCheckStatus(kycSessionStore.kycSession.kycCheck.status))
    );
  }
}

function isKycCheckComplete(kycCheckStatus: KycCheckStatus) {
  return [KycCheckStatus.PASSED, KycCheckStatus.FAILED].includes(kycCheckStatus);
}

async function handleChangedKycCheckStatus(kycCheckStatus: KycCheckStatus) {
  if (isKycCheckComplete(kycCheckStatus)) {
    await redirectToFinalScreen(kycCheckStatus);
    stopPolling();
  }
}

async function redirectToFinalScreen(kycCheckStatus: KycCheckStatus) {
  if (kycCheckStatus === KycCheckStatus.PASSED) {
    await router.replace({ name: wellKnownKycSessionRoutes.Success });
  } else if (kycCheckStatus === KycCheckStatus.FAILED) {
    await router.replace({ name: wellKnownKycSessionRoutes.Failed });
  }
}

initiateCheck();

onMounted(() => {
  addEventListener('visibilitychange', visibilityChangeHandler);
});

onUnmounted(() => {
  removeEventListener('visibilitychange', visibilityChangeHandler);
});

const partnerName = kycSession?.value?.partner?.name ?? 'Immersve';
const kycRedirectURL = kycSession?.value?.redirectUrl;
</script>

<template>
  <KycSessionLayout>
    <Loading class="mx-auto mt-10 h-10 w-10" v-if="isLoading" />
    <div class="flex flex-col" data-test="screen-kyc-under-review" v-else>
      <img :src="underReview" class="mb-4 size-16" />
      <h1 class="mb-4 text-3xl">Your application is being reviewed</h1>
      <p class="text-light-secondary dark:text-dark-secondary mb-8">
        We'll let you know as soon as we've finished checking your documents. In the meantime, reach
        out to support@immersve.com if you have any questions.
      </p>
      <p v-if="kycRedirectURL" class="text-light-primary dark:text-dark-primary mb-6 font-bold">
        Go back to
        <a data-test="action-back-to-partner" :href="kycRedirectURL" class="underline">{{
          partnerName
        }}</a>
        to continue.
      </p>
    </div>
  </KycSessionLayout>
</template>
